import { useState } from "react";
import { Copy, Download, Link2Off, Loader2, Mail, MessageCircle, Share2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ExportChoices = ({ mode, pending, choose }) => <div data-testid="itinerary-export-choices">
  <div className="flex flex-col gap-2 sm:flex-row">
    <Button data-testid={`itinerary-${mode}-include`} disabled={!!pending} onClick={() => choose(true)} className="rounded-full bg-ac hover:bg-ac-hover font-bold">Include Saved Places</Button>
    <Button data-testid={`itinerary-${mode}-skip`} disabled={!!pending} onClick={() => choose(false)} variant="outline" className="rounded-full font-bold">Skip Saved Places</Button>
  </div>
</div>;

const ItineraryLinkActions = ({ trip, url, setNotice }) => {
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setNotice("Link copied — paste it into any conversation."); }
    catch { setNotice("Select the link and copy it manually."); }
  };
  const share = async () => {
    try { await navigator.share({ title: trip.title, text: `My itinerary: ${trip.title}`, url }); }
    catch (error) { if (error.name !== "AbortError") setNotice("Use Email, Messages, or Copy link to share this itinerary."); }
  };
  const message = encodeURIComponent(`Here's my itinerary, ${trip.title}:\n${url}`);
  const smsPrefix = /iPad|iPhone|iPod/.test(navigator.userAgent) ? "sms:&body=" : "sms:?body=";
  return <div className="min-w-0 space-y-3">
    <label htmlFor="itinerary-share-url" className="block text-xs font-bold uppercase tracking-wider text-stone-500">Read-only link</label>
    <div className="flex min-w-0 flex-wrap gap-2">
      <Input id="itinerary-share-url" data-testid="itinerary-share-url" value={url} readOnly onFocus={(event) => event.target.select()} className="min-w-0 flex-1 basis-48 rounded-xl text-sm" />
      <Button data-testid="itinerary-share-copy" onClick={copy} variant="outline" className="rounded-xl font-bold"><Copy className="h-4 w-4" />Copy link</Button>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <Button asChild variant="outline" className="rounded-full font-bold"><a data-testid="itinerary-share-email" href={`mailto:?subject=${encodeURIComponent(`Itinerary: ${trip.title}`)}&body=${message}`}><Mail className="h-4 w-4" />Email</a></Button>
      <Button asChild variant="outline" className="rounded-full font-bold"><a data-testid="itinerary-share-messages" href={`${smsPrefix}${message}`}><MessageCircle className="h-4 w-4" />Messages</a></Button>
    </div>
    {typeof navigator.share === "function" && <Button data-testid="itinerary-share-device" onClick={share} className="w-full rounded-full bg-ac hover:bg-ac-hover font-bold"><Share2 className="h-4 w-4" />More sharing options</Button>}
  </div>;
};

export const ItineraryExports = ({ trip, disabled }) => {
  const [mode, setMode] = useState(null);
  const [status, setStatus] = useState(null);
  const [choice, setChoice] = useState(null);
  const [pending, setPending] = useState("");
  const [error, setError] = useState("");
  const [failedAction, setFailedAction] = useState("");
  const [failedChoice, setFailedChoice] = useState(false);
  const [notice, setNotice] = useState("");
  const url = mode === "share" && choice !== null && status?.token ? new URL(`/shared/itinerary/${status.token}`, process.env.REACT_APP_BACKEND_URL).href : "";

  const loadStatus = async () => {
    setPending("Loading sharing status…"); setError("");
    try { const { data } = await api.get(`/trips/${trip.id}/share`); setStatus(data); }
    catch { setFailedAction("load"); setError("Could not load sharing status. Please try again."); }
    finally { setPending(""); }
  };
  const open = (action) => {
    setMode(action); setChoice(null); setStatus(null); setError(""); setNotice("");
    if (action === "share") loadStatus();
  };
  const choose = async (include) => {
    setPending(mode === "share" ? "Creating your link…" : "Preparing your PDF…");
    setError(""); setNotice(""); setFailedChoice(include);
    try {
      if (mode === "share") {
        const { data } = await api.post(`/trips/${trip.id}/share`, { include_saved_places: include });
        setStatus(data); setChoice(include);
      } else {
        const { data } = await api.get(`/trips/${trip.id}/pdf`, { params: { include_saved_places: include }, responseType: "blob" });
        const objectUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = `${trip.title.replace(/[<>:"/\\|?*\u0000-\u001F]/g, "").trim() || "itinerary"}.pdf`;
        document.body.appendChild(anchor); anchor.click(); anchor.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        setMode(null);
      }
    } catch {
      setFailedAction("choose");
      setError(mode === "share" ? "Could not create the sharing link. Please try again." : "Could not download the PDF. Please try again.");
    } finally { setPending(""); }
  };
  const stop = async () => {
    setPending("Stopping sharing…"); setError(""); setNotice("");
    try {
      await api.delete(`/trips/${trip.id}/share`);
      setStatus(null); setChoice(null); setNotice("Sharing stopped. Previously shared links no longer work.");
    } catch { setFailedAction("stop"); setError("Could not confirm that sharing stopped. Please try again."); }
    finally { setPending(""); }
  };
  return <>
    <Button data-testid="share-itinerary-btn" aria-label="Share itinerary" title="Share" disabled={disabled} onClick={() => open("share")} variant="outline" className="h-9 w-9 sm:h-10 sm:w-auto p-0 sm:px-4 flex-none rounded-full font-bold text-stone-700"><Share2 aria-hidden="true" className="h-4 w-4" /><span className="hidden sm:inline">Share</span></Button>
    <Button data-testid="download-itinerary-btn" aria-label="Download itinerary PDF" title="Download PDF" disabled={disabled} onClick={() => open("pdf")} variant="outline" className="h-9 w-9 sm:h-10 sm:w-auto p-0 sm:px-4 flex-none rounded-full font-bold text-stone-700"><Download aria-hidden="true" className="h-4 w-4" /><span className="hidden sm:inline">Download PDF</span></Button>
    <Dialog open={!!mode} onOpenChange={(next) => { if (!next && !pending) setMode(null); }}>
      <DialogContent data-testid="itinerary-export-dialog" aria-describedby={undefined} className="w-[calc(100%-2rem)] max-w-lg min-w-0 max-h-[88vh] overflow-y-auto rounded-3xl font-['Figtree']">
        <DialogHeader className="min-w-0 pr-5 text-left">
          <DialogTitle data-testid="itinerary-export-title" className="font-['Nunito'] text-xl break-words">{mode === "share" ? "Share your itinerary" : "Download your itinerary"}</DialogTitle>
        </DialogHeader>
        {pending && <p data-testid="itinerary-export-loading" role="status" className="flex items-center gap-2 text-sm text-stone-500"><Loader2 className="h-4 w-4 animate-spin" />{pending}</p>}
        {error && <div className="space-y-2"><p data-testid="itinerary-export-error" role="alert" className="text-sm text-rose-600">{error}</p><Button data-testid="itinerary-export-retry" disabled={!!pending} onClick={() => failedAction === "load" ? loadStatus() : failedAction === "stop" ? stop() : choose(failedChoice)} variant="outline" className="rounded-full">Try again</Button></div>}
        {!url && !(mode === "share" && error && failedAction === "load") && <ExportChoices mode={mode} pending={pending} choose={choose} />}
        {mode === "share" && !url && status?.token && <p data-testid="itinerary-share-existing-warning" className="text-xs text-stone-500">Sharing is already on. Changing the Saved Places choice replaces the old link, which will stop working.</p>}
        {url && !pending && <ItineraryLinkActions trip={trip} url={url} setNotice={setNotice} />}
        {mode === "share" && status?.token && !pending && <div className="border-t border-stone-200 pt-3 space-y-2"><Button data-testid="itinerary-share-stop" onClick={stop} variant="outline" className="w-full rounded-full font-bold text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"><Link2Off className="h-4 w-4" />Stop sharing</Button></div>}
        {notice && <p data-testid="itinerary-export-notice" role="status" className="text-sm text-ac-softfg">{notice}</p>}
      </DialogContent>
    </Dialog>
  </>;
};
